"use client";
import OverlayWrapper from "@/components/ui/OverLayWrapper";
import React, { useEffect, useRef } from "react";
import { useState } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { usePublicSignupEnabled } from "@/components/auth/PublicSignupProvider";
import SignupUnavailable from "@/components/auth/SignupUnavailable";
import { signupFormSchema } from "@/zod/auth.schema";
import arrow2 from "@/../public/assets/images/arrow2.svg";
import Image from "next/image";
import LangButton from "@/components/ui/LangButton";
import LabeledInput from "@/components/ui/LabeledInput";
import toast from "react-hot-toast";
import imageCompression from "browser-image-compression";
import { PROFILE_IMAGE_COMPRESSION } from "@/libs/profile-image";
import DotPulseLdr from "@/components/ui/DotPulseLdr";
import clsx from "clsx";
import ImageCropperModal from "@/components/ui/ImageCropperModal";
import CustomToast from "@/components/ui/CustomToast";
import { UserSVG } from "@/components/ui/SVG";
import { useLocaleState } from "@/hooks/useLocaleState";
import CaptchaField, {
  CaptchaFieldHandle,
} from "@/components/ui/CaptchaField";
import { X } from "lucide-react";
import PasswordRequirements from "@/components/ui/PasswordRequirements";

const translations = {
  en: {
    title: "Sign up",
    subtitle: "Create your FARBIN account",
    imageText: "Upload photo (optional)",
    uploadButton: "Edit",
    email: "Email",
    password: "Password",
    confirmPassword: "Confirm Password",
    name: "Name",
    surName: "Sur Name",
    codeInput: "Enter code",
    codeButton: "Send code",
    codeSubtitle: "Check your email box for confirmation code",
    codeSent: "Sent",
    submitButton: "Sign up",
    loadingButton: "Signing up...",
    captchaPlaceholder: "Enter the captcha",
    refreshCaptcha: "Refresh captcha",
  },
  fa: {
    title: "ایجاد حساب کاربری جدید",
    subtitle: "",
    imageText: "بارگزاری تصویر (اختیاری)",
    uploadButton: "ویرایش",
    email: "ایمیل",
    password: "رمز عبور",
    confirmPassword: "تکرار رمز عبور",
    name: "نام",
    surName: "نام خانوادگی",
    codeInput: "ورود کد",
    codeButton: "درخواست کد",
    codeSubtitle: "پس از درخواست، صندوق پستی خود را برای دریافت کد چک نمایید",
    codeSent: "ارسال شد",
    submitButton: "ایجاد حساب کاربری",
    loadingButton: "در حال ایجاد حساب کاربری...",
  },
};

function SignupOverlay({
  onClose,
}: {
  onClose: () => void;
}) {
  const { changeLocale, currentLang, setCurrentLang } = useLocaleState();
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const [name, setName] = useState("");
  const [surName, setSurName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [repeatPassword, setRepeatPassword] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [captcha, setCaptcha] = useState("");
  const [failedAttempts, setFailedAttempts] = useState(0);

  const signupEnabled = usePublicSignupEnabled();
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isImageCompressing, setIsImageCompressing] = useState(false);
  const [isCropModalOpen, setIsCropModalOpen] = useState(false);
  const [cropImageSrc, setCropImageSrc] = useState("");

  const [errors, setErrors] = useState<{
    name?: string;
    surName?: string;
    email?: string;
    password?: string;
    repeat_password?: string;
    image?: string;
    verification_code?: string;
    code_server?: string;
    captcha?: string;
    server?: string;
  }>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isCodeLoading, setIsCodeLoading] = useState(false);
  const [isCodeSent, setIsCodeSent] = useState(false);
  const [isError, setIsError] = useState(false);
  const callbackUrl = searchParams.get("callbackUrl") || pathname || "/";
  const t = translations[currentLang];
  const captchaRef = useRef<CaptchaFieldHandle>(null);
  const showCaptcha = true;

  useEffect(() => {
    if (
      errors.name ||
      errors.surName ||
      errors.email ||
      errors.password ||
      errors.repeat_password ||
      errors.image ||
      errors.verification_code ||
      errors.code_server ||
      errors.captcha ||
      errors.server
    ) {
      setIsError(true);
    } else {
      setIsError(false);
    }
  }, [errors]);

  //handlers
  // --- 2. UPDATED Image Handling ---
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setErrors((prev) => ({ ...prev, image: undefined }));
      const objectUrl = URL.createObjectURL(file);
      setCropImageSrc(objectUrl);
      setIsCropModalOpen(true);
      e.target.value = "";
    }
  };

  const handleCropComplete = async (croppedBlob: Blob) => {
    setIsImageCompressing(true);

    try {
      const options = PROFILE_IMAGE_COMPRESSION;
      const compressedBlob = await imageCompression(
        croppedBlob as File,
        options
      );

      const finalFile = new File([compressedBlob], "profile_image.jpg", {
        type: compressedBlob.type,
        lastModified: Date.now(),
      });

      setImageFile(finalFile);

      const previewUrl = URL.createObjectURL(finalFile);
      setImagePreview(previewUrl);
    } catch (error) {
      console.error("Compression failed:", error);
      setErrors((prev) => ({ ...prev, image: "Failed to process image." }));
    } finally {
      setIsImageCompressing(false);
      if (cropImageSrc) {
        URL.revokeObjectURL(cropImageSrc);
      }
    }
  };

  const handleSendCode = async () => {
    if (!signupEnabled) return;
    setIsCodeLoading(true);
    setErrors((prev) => ({
      ...prev,
      email: undefined,
      code_server: undefined,
      captcha: undefined,
    }));
    if (!email || !/^\S+@\S+\.\S+$/.test(email)) {
      setErrors((prev) => ({
        ...prev,
        email: "Please enter a valid email first.",
      }));
      setIsCodeLoading(false);
      return;
    }
    if (showCaptcha && !captcha.trim()) {
      setErrors((prev) => ({
        ...prev,
        captcha: "Captcha is required.",
      }));
      setIsCodeLoading(false);
      return;
    }

    try {
      const res = await fetch("/api/auth/send-verification", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, captcha: showCaptcha ? captcha : undefined }),
      });

      const data = await res.json();
      if (!res.ok) {
        setFailedAttempts((prev) => prev + 1);
        setErrors((prev) => ({
          ...prev,
          code_server: data.message || "Failed to send code.",
        }));
      } else {
        setFailedAttempts(0);
        setIsCodeSent(true);
        toast.custom((t) => (
          <CustomToast
            t={t}
            message="Verification code sent to your email!"
            type="success"
          />
        ));
      }
    } catch (error) {
      console.log(error);
      setErrors((prev) => ({
        ...prev,
        code_server: "An unexpected error occurred.",
      }));
    } finally {
      setIsCodeLoading(false);
      if (showCaptcha || failedAttempts + 1 >= 2) {
        captchaRef.current?.reset();
        setCaptcha("");
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!signupEnabled) return;
    setIsLoading(true);
    setErrors({});

    const validationResult = signupFormSchema.safeParse({
      name,
      sur_name: surName,
      email,
      password,
      repeat_password: repeatPassword,
      image: imageFile ?? undefined,
      verification_code: verificationCode,
      captcha: showCaptcha ? captcha : undefined,
    });

    if (!validationResult.success) {
      const rawErrors = validationResult.error.flatten().fieldErrors;

      const formattedErrors = Object.fromEntries(
        Object.entries(rawErrors).map(([key, value]) => {
          const normalizedKey = key === "sur_name" ? "surName" : key;
          return [normalizedKey, value?.[0]];
        })
      );

      setErrors(formattedErrors);
      setIsLoading(false);
      return;
    }

    const formValues = validationResult.data;

    const formData = new FormData();
    Object.entries(formValues).forEach(([key, value]) => {
      if (key === "image" && value instanceof File) {
        formData.append(key, value, value.name);
      } else if (value) {
        formData.append(key, value as string);
      }
    });
    formData.append("locale", currentLang);

    try {
      const res = await fetch("/api/auth/signup", {
        method: "POST",
        body: formData,
      });

      const data = await res.json();
      if (!res.ok) {
        setFailedAttempts((prev) => prev + 1);
        setErrors({ server: data.message || "Something went wrong." });
      } else {
        setFailedAttempts(0);
        toast.custom((t) => (
          <CustomToast
            t={t}
            message="Account created successfully!"
            type="success"
          />
        ));
        router.replace(
          `/${currentLang}/signin?callbackUrl=${encodeURIComponent(callbackUrl)}`,
        );
        router.refresh();
        onClose();
      }
    } catch (error) {
      console.log(error);
      setErrors({ server: "An unexpected error occurred." });
    } finally {
      setIsLoading(false);
      if (showCaptcha || failedAttempts + 1 >= 2) {
        captchaRef.current?.reset();
        setCaptcha("");
      }
    }
  };
  if (!signupEnabled) return <SignupUnavailable locale={currentLang} />;

  return (
    <>
      <ImageCropperModal
        isOpen={isCropModalOpen}
        onClose={() => setIsCropModalOpen(false)}
        imageSrc={cropImageSrc}
        onCropComplete={handleCropComplete}
      />
      <OverlayWrapper
        onClose={onClose}
        snapPoints={["93vh"]}
        className="bg-grayCustom"
        draggable={false}
      >
        <div className="flex flex-col items-start justify-center px-2 pb-24 ">
          <div className="sticky top-2 z-30 w-full bg-white flex justify-between p-2">
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="flex h-9 w-9 items-center justify-center rounded-full bg-grayCustom text-slateCustom transition hover:text-pinkCustom"
            >
              <X className="h-4 w-4" strokeWidth={4} />
            </button>
            <LangButton
              currentLang={currentLang}
              setCurrentLang={setCurrentLang}
              bgColor="grayCustom"
              onLocaleChange={changeLocale}
            />
          </div>
          <div className="max-w-md w-full bg-white rounded p-6 ">
            <h1
              dir={currentLang === "fa" ? "rtl" : "ltr"}
              className="relative text-xl font-semibold leading-tight mt-0 mb-8"
            >
              {t.title}
              <div
                className={`absolute ${
                  currentLang === "fa" ? "-right-[14px]" : "-left-[14px]"
                } top-3`}
              >
                <Image
                  className="w-3"
                  src={arrow2}
                  alt="arrow"
                  style={{
                    transform: currentLang === "fa" ? "scaleX(-1)" : "none",
                  }}
                />
              </div>
              <p className="text-xs font-light text-gray-400 mt-2">
                {t.subtitle}
              </p>
            </h1>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="flex flex-col items-center justify-center">
                {isImageCompressing ? (
                  <div className="w-24 h-24 rounded-sm bg-gray-200 flex items-center justify-center text-xs text-gray-500">
                    <DotPulseLdr size={30} speed={2} color="gray" />
                  </div>
                ) : imagePreview ? (
                  <Image
                    src={imagePreview}
                    alt="Profile preview"
                    width={60}
                    height={60}
                    className="w-24 h-24 rounded-sm object-cover"
                  />
                ) : (
                  <UserSVG className="w-24" />
                )}

                <span className="text-xs font-light text-gray-400 mt-2">
                  {t.imageText}
                </span>
                <label
                  htmlFor="image-upload"
                  className="cursor-pointer px-5 py-1 mt-3 text-slateCustom text-sm rounded-sm border border-slateCustom"
                >
                  {t.uploadButton}
                </label>
                <input
                  type="file"
                  accept="image/*"
                  name="image"
                  onChange={handleImageChange}
                  id="image-upload"
                  className="hidden"
                  disabled={isLoading}
                />
                {errors.image && (
                  <p className="text-red-500 text-xs mt-1">{errors.image}</p>
                )}
              </div>
              <LabeledInput
                label={t.name}
                name="name"
                type="text"
                value={name}
                className="w-full bg-white"
                onChange={(e) => setName(e.target.value)}
                error={errors.name}
                disabled={isLoading}
              />
              <LabeledInput
                label={t.surName}
                name="surName"
                type="text"
                value={surName}
                className="w-full bg-white"
                onChange={(e) => setSurName(e.target.value)}
                error={errors.surName}
                disabled={isLoading}
              />
              <LabeledInput
                label={t.email}
                name="email"
                type="email"
                value={email}
                className="w-full bg-white"
                onChange={(e) => setEmail(e.target.value)}
                error={errors.email}
                disabled={isLoading}
              />
              <LabeledInput
                label={t.password}
                name="password"
                type="password"
                value={password}
                className="w-full bg-white"
                onChange={(e) => setPassword(e.target.value)}
                error={errors.password}
                disabled={isLoading}
              />
              <PasswordRequirements password={password} />
              <LabeledInput
                label={t.confirmPassword}
                name="confirmPassword"
                type="password"
                value={repeatPassword}
                className="w-full bg-white"
                onChange={(e) => setRepeatPassword(e.target.value)}
                error={errors.repeat_password}
                disabled={isLoading}
              />

              <div className="space-y-1">
                <div className="flex py-4 px-4 justify-between  border border-gray-300 rounded-md focus-within:border-slateCustom">
                  <input
                    type="text"
                    name="verification_code"
                    placeholder={t.codeInput}
                    className="focus:outline-none w-full"
                    value={verificationCode}
                    autoComplete="off"
                    onChange={(e) => setVerificationCode(e.target.value)}
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    onClick={handleSendCode}
                    disabled={isCodeLoading || isLoading}
                    className="border-l border-gray-300 text-slateCustom pl-4 text-sm whitespace-nowrap disabled:opacity-50"
                  >
                    {isCodeLoading ? (
                      <DotPulseLdr size={20} speed={1.3} color="blue" />
                    ) : isCodeSent ? (
                      t.codeSent
                    ) : (
                      t.codeButton
                    )}
                  </button>
                </div>
                {errors.verification_code && (
                  <p className="text-red-500 text-xs">
                    {errors.verification_code}
                  </p>
                )}
                {errors.code_server && (
                  <p className="text-red-500 text-xs">{errors.code_server}</p>
                )}
                <p
                  className={clsx(
                    "text-xs font-light text-gray-400 mt-2 px-1",
                    currentLang === "fa" && "text-right"
                  )}
                >
                  {t.codeSubtitle}
                </p>
              </div>
              {showCaptcha && (
                <CaptchaField
                  ref={captchaRef}
                  value={captcha}
                  onChange={setCaptcha}
                  onClearError={() =>
                    setErrors((prev) => ({ ...prev, captcha: undefined }))
                  }
                  disabled={isLoading || isCodeLoading}
                  error={errors.captcha}
                  placeholder="Enter the captcha"
                  refreshLabel="Refresh captcha"
                />
              )}
              <button
                type="submit"
                className={clsx(
                  "bg-slateCustom w-full text-white font-bold py-2 px-4 rounded-md",
                  isError && "border-2 border-red-500"
                )}
                disabled={isLoading || verificationCode.length < 1}
              >
                {isLoading ? (
                  <DotPulseLdr size={40} speed={1.3} color="white" />
                ) : (
                  t.submitButton
                )}
              </button>
              {errors.server && (
                <p className="text-red-500 text-center text-sm">
                  {errors.server}
                </p>
              )}
            </form>
          </div>
        </div>
      </OverlayWrapper>
    </>
  );
}

export default SignupOverlay;
